/**
 * ResponseOptions - Displays clickable response options for checkpoint conversations
 *
 * Handles:
 * - Rendering response buttons with icons
 * - Text input expansion for text/text_homework actions
 * - Visual feedback on hover/press
 */
import React, { useRef, useState } from "react"
import { View, StyleSheet, TouchableOpacity } from "react-native"
import Animated, { FadeInDown } from "react-native-reanimated"
import { FontAwesome5 } from "@expo/vector-icons"
import { LinearGradient } from "expo-linear-gradient"
import moment from "moment"
import DatePickerModalM from "@common/components/modal/DatePickerModalM"
import { theme } from "@common/theme/theme"
import TextM from "@common/components/text/TextM"
import UncontrolledTextInputM from "@common/components/input/UncontrolledTextInputM"
import { ListGroupM } from "@common/components/list/ListGroupM"
import ClassChipGridM from "@common/components/funnel/ClassChipGridM"
import { isCompactChoice } from "./compactChoice"
import { RESPONSE_ACTIONS } from "../../../../engine/precall/checkpoint/constants"

// Icon mapping for response actions
const ACTION_ICONS = {
    [RESPONSE_ACTIONS.PHOTO]: { name: "camera", color: theme.colors.primary },
    [RESPONSE_ACTIONS.ACTIVITY]: { name: "tasks", color: theme.colors.success },
    [RESPONSE_ACTIONS.TEXT]: { name: "edit", color: theme.colors.purple },
    [RESPONSE_ACTIONS.TEXT_HOMEWORK]: { name: "edit", color: theme.colors.purple },
    [RESPONSE_ACTIONS.DATE]: { name: "calendar-alt", color: theme.colors.primary }
}

/**
 * @param {Object} props
 * @param {Array} props.options - Array of response options
 * @param {Function} props.onSelect - Callback when a response is selected
 * @param {Function} props.onTextSubmit - Callback when text is submitted
 * @param {Function} props.onDateSubmit - Callback when a date is selected
 * @param {Object} props.textInput - Current text input state { index, value }
 * @param {Function} props.onTextInputChange - Callback when text input changes
 * @param {number} props.width - Width of the component
 */
const ResponseOptions = ({
    options = [],
    onSelect,
    onTextSubmit,
    onDateSubmit,
    textInput = { index: null, value: "" },
    onTextInputChange,
    width
}) => {
    const textInputRef = useRef(null)
    const [hoveredItem, setHoveredItem] = React.useState(null)
    const [datePickerVisible, setDatePickerVisible] = useState(false)
    const [datePickerItem, setDatePickerItem] = useState(null)

    if (!options?.length) return null

    const handleItemPress = (item, index) => {
        const isTextAction = item.action === RESPONSE_ACTIONS.TEXT ||
                             item.action === RESPONSE_ACTIONS.TEXT_HOMEWORK

        if (item.action === RESPONSE_ACTIONS.DATE) {
            setDatePickerItem(item)
            setDatePickerVisible(true)
        } else if (isTextAction) {
            onTextInputChange?.({ index, value: textInput.value })
            textInputRef.current?.focus?.()
        } else {
            onSelect?.(item)
        }
    }

    const handleDateConfirm = (date) => {
        setDatePickerVisible(false)
        if (datePickerItem) onDateSubmit?.(datePickerItem, date)
        setDatePickerItem(null)
    }

    const handleTextSubmit = (item) => {
        if (textInput.value.trim()) {
            onTextSubmit?.(item, textInput.value)
        }
    }

    const renderResponseItem = ({ item, index }) => {
        const icon = ACTION_ICONS[item.action]
        const isTextAction = item.action === RESPONSE_ACTIONS.TEXT ||
                             item.action === RESPONSE_ACTIONS.TEXT_HOMEWORK
        const isExpanded = isTextAction && textInput.index === index

        return (
            <Animated.View entering={FadeInDown.delay(index * 100).duration(400).springify()}>
                <TouchableOpacity
                    onPress={() => handleItemPress(item, index)}
                    onPressIn={() => setHoveredItem(index)}
                    onPressOut={() => setHoveredItem(null)}
                    style={[
                        styles.responseItem,
                        hoveredItem === index && styles.responseItemHovered
                    ]}
                    activeOpacity={0.8}
                >
                    <View style={styles.responseContent}>
                        <TextM
                            fontWeight="SemiBold"
                            color={theme.colors.dark}
                            wrap
                            style={{ flex: 1 }}
                        >
                            {item.body}
                        </TextM>
                        {icon && (
                            <View style={[
                                styles.iconBadge,
                                { backgroundColor: `${icon.color}20` }
                            ]}>
                                <FontAwesome5
                                    name={icon.name}
                                    size={18}
                                    color={icon.color}
                                />
                            </View>
                        )}
                    </View>
                </TouchableOpacity>

                {/* Text input expansion */}
                {isExpanded && (
                    <Animated.View
                        entering={FadeInDown.duration(350)}
                        style={styles.textInputContainer}
                    >
                        <View style={styles.textInputBlur}>
                            <UncontrolledTextInputM
                                ref={textInputRef}
                                placeholder="Votre réponse ici..."
                                value={textInput.value}
                                onChangeText={(value) =>
                                    onTextInputChange?.({ index, value })
                                }
                                style={styles.textInput}
                                contentContainerStyle={{ flex: 1, width: undefined }}
                                returnKeyType="send"
                                onSubmitEditing={() => handleTextSubmit(item)}
                            />
                            <TouchableOpacity
                                onPress={() => handleTextSubmit(item)}
                                disabled={!textInput.value.trim()}
                            >
                                <LinearGradient
                                    colors={
                                        textInput.value.trim()
                                            ? [theme.colors.primary, theme.colors.purple]
                                            : ["rgba(156,163,175,0.5)", "rgba(156,163,175,0.3)"]
                                    }
                                    style={styles.sendButton}
                                >
                                    <FontAwesome5
                                        name="paper-plane"
                                        size={16}
                                        color={theme.colors.white}
                                    />
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>
                    </Animated.View>
                )}
            </Animated.View>
        )
    }

    // Les dix classes du sous-scénario « affirmer sa classe » : la grille de
    // pastilles du funnel — le MÊME composant que les écrans de choix de classe
    // de l'inscription, du stage et de l'été, pour que la question ait la même
    // écriture visuelle partout où on la pose. Le prédicat et ses bords vivent
    // dans `compactChoice.js`, avec leur test.
    if (isCompactChoice(options)) {
        return (
            <Animated.View
                entering={FadeInDown.delay(200).duration(400)}
                style={[styles.listGroupContainer, { width }]}
            >
                <ClassChipGridM
                    // Une pastille est un envoi, pas une sélection persistante :
                    // le tour se termine dessus, donc `selectedIds` reste vide.
                    options={options.map((item, index) => ({
                        id: index,
                        label: item?.body
                    }))}
                    selectedIds={[]}
                    onToggle={(index) => handleItemPress(options[index], index)}
                    style={styles.chipGrid}
                />
            </Animated.View>
        )
    }

    return (
        <Animated.View
            entering={FadeInDown.delay(200).duration(400)}
            style={[styles.listGroupContainer, { width }]}
        >
            <ListGroupM
                options={options}
                renderItem={renderResponseItem}
                itemHeight={60}
                style={styles.listGroup}
                animation
                scrollEnabled={false}
            />
            <DatePickerModalM
                visible={datePickerVisible}
                onDismiss={() => setDatePickerVisible(false)}
                value={moment().format("YYYY-MM-DD")}
                onConfirm={handleDateConfirm}
                label="Date du contrôle"
                saveLabel="Valider"
                validRange={{ startDate: new Date() }}
                allowEditing
                applyOnSaveOnly
            />
        </Animated.View>
    )
}

const styles = StyleSheet.create({
    listGroupContainer: {
        backgroundColor: "transparent",
        justifyContent: "center",
        alignItems: "center",
        marginTop: 20
    },
    listGroup: {
        width: "100%",
        backgroundColor: "transparent"
    },
    // ClassChipGridM porte le style des pastilles ; on ne surcharge que le
    // centrage, la bulle de Sam étant plus étroite qu'un écran de funnel.
    chipGrid: {
        justifyContent: "center"
    },
    responseItem: {
        marginVertical: 8,
        borderRadius: 18,
        overflow: "hidden",
        backgroundColor: theme.colors.white,
        shadowColor: theme.colors.grey,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 4
    },
    responseContent: {
        padding: 18,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        backgroundColor: theme.colors.white,
        minHeight: 60
    },
    responseItemHovered: {
        transform: [{ scale: 1.02 }]
    },
    iconBadge: {
        padding: 10,
        borderRadius: 12
    },
    textInputContainer: {
        marginTop: 8,
        marginHorizontal: 8,
        borderRadius: 16
    },
    textInputBlur: {
        flexDirection: "row",
        alignItems: "center",
        padding: 8,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: "rgba(156, 163, 175, 0.3)",
        backgroundColor: "rgba(255, 255, 255, 0.95)"
    },
    textInput: {
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        borderRadius: 12,
        fontSize: 15,
        paddingHorizontal: 14,
        paddingVertical: 10,
        minHeight: 42
    },
    sendButton: {
        padding: 12,
        borderRadius: 12,
        marginLeft: 8
    }
})

export default ResponseOptions
